import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { RoleOutlet } from './RoleOutlet';
import { RoleRouter } from './RoleRouter';
import { AsyncBoundary } from '@/components/AsyncBoundary';
import { ErrorBoundary } from '@/components/ErrorBoundary';

const LoginPage = lazy(() =>
  import('@/features/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const NotFoundPage = lazy(() =>
  import('@/features/common/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

const SuperOverviewPage = lazy(() =>
  import('@/features/super-admin/pages/SuperOverviewPage').then((m) => ({
    default: m.SuperOverviewPage,
  })),
);
const PlatformWorkforceHealthLayout = lazy(() =>
  import('@/features/super-admin/pages/health/PlatformWorkforceHealthLayout').then((m) => ({
    default: m.PlatformWorkforceHealthLayout,
  })),
);
const OrganizationsListPage = lazy(() =>
  import('@/features/super-admin/pages/OrganizationsListPage').then((m) => ({
    default: m.OrganizationsListPage,
  })),
);
const ProvisionOrgPage = lazy(() =>
  import('@/features/super-admin/pages/ProvisionOrgPage').then((m) => ({
    default: m.ProvisionOrgPage,
  })),
);
const OrganizationDetailPage = lazy(() =>
  import('@/features/super-admin/pages/OrganizationDetailPage').then((m) => ({
    default: m.OrganizationDetailPage,
  })),
);
const LicenseOverviewPage = lazy(() =>
  import('@/features/super-admin/pages/LicenseOverviewPage').then((m) => ({
    default: m.LicenseOverviewPage,
  })),
);
const AuditLogPage = lazy(() =>
  import('@/features/super-admin/pages/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
);

const OrgOverviewPage = lazy(() =>
  import('@/features/org-admin/pages/OrgOverviewPage').then((m) => ({
    default: m.OrgOverviewPage,
  })),
);
const EmployeesListPage = lazy(() =>
  import('@/features/org-admin/pages/EmployeesListPage').then((m) => ({
    default: m.EmployeesListPage,
  })),
);
const WorkforceHealthLayout = lazy(() =>
  import('@/features/org-admin/pages/health/WorkforceHealthLayout').then((m) => ({
    default: m.WorkforceHealthLayout,
  })),
);
const HealthOverviewTab = lazy(() =>
  import('@/features/org-admin/pages/health/OverviewTab').then((m) => ({ default: m.OverviewTab })),
);
const HealthRiskMapTab = lazy(() =>
  import('@/features/org-admin/pages/health/RiskMapTab').then((m) => ({ default: m.RiskMapTab })),
);
const HealthIndicesTab = lazy(() =>
  import('@/features/org-admin/pages/health/IndicesTab').then((m) => ({ default: m.IndicesTab })),
);
const HealthDepartmentsTab = lazy(() =>
  import('@/features/org-admin/pages/health/DepartmentsTab').then((m) => ({
    default: m.DepartmentsTab,
  })),
);
const HealthEngagementTab = lazy(() =>
  import('@/features/org-admin/pages/health/EngagementTab').then((m) => ({
    default: m.EngagementTab,
  })),
);
const HealthRoiTab = lazy(() =>
  import('@/features/org-admin/pages/health/RoiTab').then((m) => ({ default: m.RoiTab })),
);
const BulkImportPage = lazy(() =>
  import('@/features/org-admin/pages/BulkImportPage').then((m) => ({ default: m.BulkImportPage })),
);
const InvitationsPage = lazy(() =>
  import('@/features/org-admin/pages/InvitationsPage').then((m) => ({
    default: m.InvitationsPage,
  })),
);
const OrgSettingsPage = lazy(() =>
  import('@/features/org-admin/pages/OrgSettingsPage').then((m) => ({
    default: m.OrgSettingsPage,
  })),
);

const withBoundary = (node: React.ReactNode) => <AsyncBoundary>{node}</AsyncBoundary>;

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/app" replace /> },
  { path: '/login', element: withBoundary(<LoginPage />) },
  { path: '/forgot-password', element: withBoundary(<ForgotPasswordPage />) },
  {
    path: '/reset-password',
    element: (
      <ProtectedRoute>
        <AsyncBoundary>
          <ResetPasswordPage />
        </AsyncBoundary>
      </ProtectedRoute>
    ),
  },
  {
    path: '/app',
    element: (
      <ErrorBoundary>
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      </ErrorBoundary>
    ),
    children: [
      { index: true, element: <RoleRouter /> },
      {
        path: 'super',
        element: <RoleOutlet allow={['SUPER_ADMIN']} />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: withBoundary(<SuperOverviewPage />) },
          {
            path: 'health-insights',
            element: withBoundary(<PlatformWorkforceHealthLayout />),
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: withBoundary(<HealthOverviewTab />) },
              { path: 'risk-map', element: withBoundary(<HealthRiskMapTab />) },
              { path: 'indices', element: withBoundary(<HealthIndicesTab />) },
              { path: 'departments', element: withBoundary(<HealthDepartmentsTab />) },
              { path: 'engagement', element: withBoundary(<HealthEngagementTab />) },
              { path: 'roi', element: withBoundary(<HealthRoiTab />) },
            ],
          },
          { path: 'organizations', element: withBoundary(<OrganizationsListPage />) },
          { path: 'organizations/new', element: withBoundary(<ProvisionOrgPage />) },
          { path: 'organizations/:id', element: withBoundary(<OrganizationDetailPage />) },
          { path: 'licenses', element: withBoundary(<LicenseOverviewPage />) },
          { path: 'audit', element: withBoundary(<AuditLogPage />) },
        ],
      },
      {
        path: 'org',
        element: <RoleOutlet allow={['ORG_ADMIN']} />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: 'overview', element: withBoundary(<OrgOverviewPage />) },
          { path: 'employees', element: withBoundary(<EmployeesListPage />) },
          {
            path: 'health-insights',
            element: withBoundary(<WorkforceHealthLayout />),
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: withBoundary(<HealthOverviewTab />) },
              { path: 'risk-map', element: withBoundary(<HealthRiskMapTab />) },
              { path: 'indices', element: withBoundary(<HealthIndicesTab />) },
              { path: 'departments', element: withBoundary(<HealthDepartmentsTab />) },
              { path: 'engagement', element: withBoundary(<HealthEngagementTab />) },
              { path: 'roi', element: withBoundary(<HealthRoiTab />) },
            ],
          },
          { path: 'employees/import', element: withBoundary(<BulkImportPage />) },
          { path: 'invitations', element: withBoundary(<InvitationsPage />) },
          { path: 'settings', element: withBoundary(<OrgSettingsPage />) },
        ],
      },
    ],
  },
  { path: '*', element: withBoundary(<NotFoundPage />) },
]);
